import { useRef, useState } from 'react';

import useSWRInfinite, { SWRInfiniteConfiguration } from 'swr/infinite';

import { DirectMessageService } from '@/components/pages/conversations/SocialInbox/services/DirectMessageService';
import { TimeService } from '@/utils/services/TimeService';

import { ErrorCustom, get } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import type { SocialProfileType } from '@/types/socialProfileType';
import type { DMConversationMessage } from '@/components/pages/conversations/SocialInbox/types';

import { DM_CONVERSATION_MESSAGES } from '@/lib/routes/apiEndpoints';

export type DMConversationMessagesResponse = {
    data: DMConversationMessage[];
    cursor: string;
};

type UseMessagesProps = {
    activeSPID: SocialProfileType['id'];
    conversationId: string;
    limit?: number;
    options?: SWRInfiniteConfiguration<DMConversationMessagesResponse, ErrorCustom>;
    cancelToken?: AbortController;
    shouldFetch?: boolean;
};

const getKey = (
    pageIndex: number,
    previousPageData: DMConversationMessagesResponse,
    activeSPID: UseMessagesProps['activeSPID'],
    conversationId: UseMessagesProps['conversationId'],
    limit: UseMessagesProps['limit']
) => {
    // Reached the end
    if (previousPageData && !previousPageData.data) return null;

    // First page, we don't have `previousPageData`
    const url = `${injectEndpointUrlParams(DM_CONVERSATION_MESSAGES, {
        SPID: activeSPID,
        CONVERSATION_ID: conversationId
    })}?per_page=${limit}`;

    if (pageIndex === 0) return url;

    // Next pages, include the cursor
    if (previousPageData?.cursor) {
        return `${url}&cursor=${previousPageData.cursor}`;
    }

    // No more pages
    return null;
};

export const useDMConversationMessages = ({
    activeSPID,
    cancelToken,
    conversationId,
    limit = 20,
    options = {},
    shouldFetch = true
}: UseMessagesProps) => {
    const [isFetching, setIsFetching] = useState(false);

    const lastFetchedConversationId = useRef(conversationId);

    const triggerFetchJob = async () => {
        setIsFetching(true);
        await DirectMessageService.initiateConversationMessageFetchJob(activeSPID, conversationId);
        setIsFetching(false);
    };

    const getMessages = async (url: string) => {
        const options = { signal: cancelToken?.signal };
        const { data } = await get<{ data: DMConversationMessagesResponse }>(
            url,
            undefined,
            options
        );
        return data;
    };

    const { data, isLoading, size, mutate, setSize, error, isValidating } = useSWRInfinite<
        DMConversationMessagesResponse,
        ErrorCustom
    >(
        shouldFetch
            ? (pageIndex, previousPageData) =>
                  getKey(pageIndex, previousPageData, activeSPID, conversationId, limit)
            : null,
        async (url) => {
            let response = await getMessages(url);

            /**
             * Trigger the fetch job only for the first page
             * if the data is empty
             */
            if (
                !url.includes('cursor') &&
                !response?.data?.length &&
                lastFetchedConversationId.current !== conversationId
            ) {
                lastFetchedConversationId.current = conversationId;

                await triggerFetchJob();

                response = await getMessages(url);
            }

            return response;
        },
        {
            shouldRetryOnError: false,
            revalidateOnFocus: false,
            revalidateOnReconnect: false,
            revalidateIfStale: false,
            ...options
        }
    );

    const loadMore = () => {
        setSize(size + 1);
    };

    const isLoadingMore = size > 0 && data && typeof data[size - 1] === 'undefined' && isValidating;
    const processedMessages = data
        ? data
              .flatMap((page) => page.data)
              .sort((a, b) => TimeService.sortDates(a.sent_at, b.sent_at, 'asc'))
        : [];

    return {
        data: processedMessages,
        mutate,
        error,
        loadMore,
        isLoading: isFetching || isLoading,
        isLoadingMore,
        isLoadMore: data && typeof data[data.length - 1]?.cursor === 'string'
    };
};
